import React, { useState, useEffect } from "react";
import Main from "../../components/Main";
import TabelaDinamica from "../../components/TabelaDinamica";
import MensagemErro from "../../components/MensagemErro";
import RequestPageIcon from "@mui/icons-material/RequestPage";
import CRUDPageHeader from "../../components/CRUDPageHeader";
import {
  ajustarSomatoriasFolhaManual,
  editarFolhaManual,
  obterFolhasManuais,
  obterFolhasManuaisPorTexto,
  obterSomatoriasFolhasManuais,
} from "../../services/api-esocial.service";
import { formatToBRL } from "src/utils/currency.util";
import EditableBRLCurrencyField from "../../components/EditableBRLCurrencyField";
import styles from "./styles.module.css";
import { Grid } from "@mui/material";
import GenericTextField from "../../components/GenericTextField";
import { AssuredWorkload, BorderColor } from "@mui/icons-material";
import { BRLCurrencyFixedFormat } from "../../components/BRLCurrencyFixedFormat";
import { simplifyString } from "../../utils/simplify_string";

const FolhasManuais = () => {
  const [baseCalculo, setBaseCalculo] = useState(0);
  const [provento, setProvento] = useState(0);
  const [desconto, setDesconto] = useState(0);

  const [somatorioBaseCalculo, setSomatorioBaseCalculo] = useState(0);
  const [somatorioProvento, setSomatorioProvento] = useState(0);
  const [somatorioDesconto, setSomatorioDesconto] = useState(0);

  const [diferencaBaseCalculo, setDiferencaBaseCalculo] = useState(0);
  const [diferencaProvento, setDiferencaProvento] = useState(0);
  const [diferencaDesconto, setDiferencaDesconto] = useState(0);

  const [data, setData] = useState([]);
  const [dataInicio, setDataInicio] = useState("");
  const [grupoECnpj, setGrupoECnpj] = useState({});
  const [openSnackbarErro, setOpenSnackbarErro] = useState(false);
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const [exportFilename, setExportFilename] = useState("");
  const [exportData, setExportData] = useState([]);

  const columns = [
    {
      id: "idTabelaRubrica",
      label: "ID da Tabela\nde Rubricas",
      field: "idTabelaRubrica",
    },
    { id: "codRubrica", label: "Código de\nRubrica", field: "codRubrica_cellVal" },
    { id: "descricaoRubrica", label: "Descrição", field: "descricaoRubrica" },
    { id: "tipoRubrica", label: "Tipo de\nRubrica", field: "tipoRubrica" },
    {
      id: "valorRubrica",
      label: "Valor da\nRubrica",
      render: (row) => (
        <div>
          {'valorRubrica' in row && row.valorRubrica !== undefined ? (
            <EditableBRLCurrencyField
              value={row.valorRubrica}
              onSubmit={(value) => handleUpdateFolhaManual(row, value)}
            />
          ) : (
            <>{"Carregando..."}</>
          )}
        </div>
      ),
    },
    { id: "incidencia", label: "Incidência", field: "incidencia_cellVal" },
    {
      id: "composicaoProjeto",
      label: "Composição\nde Projeto",
      field: "composicaoProjeto",
    },
  ];

  const handleUpdateFolhaManual = async (row, value) => {
    try {
      const body = {
        folhaManual: {
          idEsocialVerbaManual: row.id_esocial_verba_manual,
          periodoApuracao: row.periodo_apurado,
          valor: value,
        },
      };
      await editarFolhaManual(grupoECnpj.CNPJ, body);
      const folhaAtualizada = data.map((folha) =>
        folha.id_esocial_verba_manual === row.id_esocial_verba_manual &&
        folha.idTabelaRubrica === row.idTabelaRubrica &&
        folha.codRubrica === row.codRubrica
          ? { ...folha, valorRubrica: value ? Number(value) : 0 }
          : folha
      );
      setData(folhaAtualizada);
      await atualizarSomatorias(folhaAtualizada);
      return true;
    } catch (err) {
      if (err.response) {
        setMsg(err.response.data.mensagem);
        handleOpenSnackbarErro();
      } else {
        setMsg("Erro: Tente novamente!");
        handleOpenSnackbarErro();
      }
      return false;
    }
  };

  const atualizarSomatorias = async (folha) => {
    let somaBC = 0;
    let somaProvento = 0;
    let somaDesconto = 0;

    folha.forEach((item) => {
      somaBC += Number(item.valorRubrica);
      if (item.tipo_rubrica === "Provento") {
        somaProvento += Number(item.valorRubrica);
      } else if (item.tipo_rubrica === "Desconto") {
        somaDesconto += Number(item.valorRubrica);
      }
    });

    setSomatorioBaseCalculo(Number(somaBC.toFixed(2)));
    setSomatorioProvento(Number(somaProvento.toFixed(2)));
    setSomatorioDesconto(Number(somaDesconto.toFixed(2)));
  };

  const handleTratarDados = async (cnpj, dados) => {
    const dadosTratados = dados.map((item) => {
      return {
        ...item,
        idTabelaRubrica: item.id_tabela_rubrica,
        codRubrica: item.codigo_rubrica,
        codRubrica_cellVal: simplifyString(item.codigo_rubrica),
        descricaoRubrica: item.descricao_rubrica,
        tipoRubrica: item.tipo_rubrica,
        valorRubrica: item.valor_rubrica ? Number(item.valor_rubrica) : 0,
        incidencia_cellVal: item.incidencia ? item.incidencia : "-",
        terceiros: item.terceiros ? `${item.terceiros} %` : " ",
        rat: item.rat ? `${item.rat} %` : " ",
        inss: item.inss ? `${item.inss} %` : " ",
        selic: item.selic ? `${item.selic} %` : " ",
        composicaoProjeto: item.composicao,
      };
    });

    setData(dadosTratados);
    await atualizarSomatorias(dadosTratados);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!dataInicio) return;
      setLoading(true);
      try {
        const response = await obterFolhasManuais(grupoECnpj.CNPJ, dataInicio);
        await handleTratarDados(grupoECnpj.CNPJ, response.folha);
        setLoading(false);
      } catch (err) {
        if (err.response) {
          setMsg(err.response.data.mensagem);
          handleOpenSnackbarErro();
        } else {
          setMsg("Erro: Tente novamente!");
          handleOpenSnackbarErro();
        }
        setLoading(false);
      }
    };

    if (grupoECnpj.CNPJ && dataInicio) {
      fetchData();
    } else {
      setData([]);
    }
    setExportFilename(getExportFilename(grupoECnpj.CNPJ));
  }, [grupoECnpj, dataInicio]);

  useEffect(() => {
    const fetchData = async () => {
      if (!dataInicio) return;
      setLoading(true);
      try {
        const response = await obterSomatoriasFolhasManuais(grupoECnpj.CNPJ, dataInicio);
        setBaseCalculo(response.somatorias.somaBaseCalculo);
        setProvento(response.somatorias.somaProvento);
        setDesconto(response.somatorias.somaDesconto);
        setLoading(false);
      } catch (err) {
        if (err.response) {
          if (err.response.status === 404) {
            setBaseCalculo(0);
            setProvento(0);
            setDesconto(0);
          } else {
            setMsg(err.response.data.mensagem);
            handleOpenSnackbarErro();
          }
        } else {
          setMsg("Erro: Tente novamente!");
          handleOpenSnackbarErro();
        }
        setLoading(false);
      }
    };

    if (grupoECnpj.CNPJ && dataInicio) {
      fetchData();
    } else {
      setSomatorioBaseCalculo(0);
      setSomatorioProvento(0);
      setSomatorioDesconto(0);
      setBaseCalculo(0);
      setProvento(0);
      setDesconto(0);
    }
  }, [data]);

  useEffect(() => {
    setExportData(setupExportData(data));
  }, [data]);

  useEffect(() => {
    setDiferencaBaseCalculo(
      Number((baseCalculo - somatorioBaseCalculo).toFixed(2))
    );
    setDiferencaProvento(Number((provento - somatorioProvento).toFixed(2)));
    setDiferencaDesconto(Number((desconto - somatorioDesconto).toFixed(2)));
  }, [somatorioBaseCalculo, baseCalculo, provento, desconto]);

  const setupExportData = (eventosFolha) => {
    var exportData = [];
    var totalVerbasProjeto = 0;
    var totalVerbasProvento = 0;
    var totalVerbasDesconto = 0;

    eventosFolha.forEach((folha) => {
      if (folha.composicaoProjeto) {
        exportData.push({
          "<style bold>ID Tabela Rubricas</style>": folha.idTabelaRubrica,
          "<style bold>Código de Rubricas</style>": folha.codRubrica,
          "<style bold>Tipo de Rubrica</style>": folha.tipoRubrica,
          "<style bold>Descrição</style>": folha.descricaoRubrica,
          "<style bold>Valor da Rubrica</style>": Number(folha.valorRubrica),
          "<style bold>Terceiros</style>": folha.terceiros,
          "<style bold>RAT</style>": folha.rat,
          "<style bold>INSS</style>": folha.inss,
          "<style bold>Selic</style>": folha.selic,
          "<style bold>Incidência</style>": folha.incidencia ? folha.incidencia : "",
        });
        totalVerbasProjeto += folha.valorRubrica;
        if (folha.tipoRubrica === "Provento") {
          totalVerbasProvento += folha.valorRubrica;
        } else if (folha.tipoRubrica === "Desconto") {
          totalVerbasDesconto += folha.valorRubrica;
        }
      }
    });

    exportData.push({
      "<style bold>ID Tabela Rubricas</style>": "",
      "<style bold>Código de Rubricas</style>": "",
      "<style bold>Tipo de Rubrica</style>": "",
      "<style bold>Descrição</style>": "<style bold>Total Provento</style>",
      "<style bold>Valor da Rubrica</style>": Number(totalVerbasProvento),
      "<style bold>Terceiros</style>": "",
      "<style bold>RAT</style>": "",
      "<style bold>INSS</style>": "",
      "<style bold>Selic</style>": "",
      "<style bold>Incidência</style>": "",
    });

    exportData.push({
      "<style bold>ID Tabela Rubricas</style>": "",
      "<style bold>Código de Rubricas</style>": "",
      "<style bold>Tipo de Rubrica</style>": "",
      "<style bold>Descrição</style>": "<style bold>Total Desconto</style>",
      "<style bold>Valor da Rubrica</style>": Number(totalVerbasDesconto),
      "<style bold>Terceiros</style>": "",
      "<style bold>RAT</style>": "",
      "<style bold>INSS</style>": "",
      "<style bold>Selic</style>": "",
      "<style bold>Incidência</style>": "",
    });

    exportData.push({
      "<style bold>ID Tabela Rubricas</style>": "",
      "<style bold>Código de Rubricas</style>": "",
      "<style bold>Tipo de Rubrica</style>": "",
      "<style bold>Descrição</style>": "<style bold>Total Projeto</style>",
      "<style bold>Valor da Rubrica</style>": Number(totalVerbasProjeto),
      "<style bold>Terceiros</style>": "",
      "<style bold>RAT</style>": "",
      "<style bold>INSS</style>": "",
      "<style bold>Selic</style>": "",
      "<style bold>Incidência</style>": "",
    });
    return exportData;
  };

  const getExportFilename = (cnpj) => {
    return "folhas_idEmpresa_"
      .concat(cnpj)
      .concat("_")
      .concat(
        new Date()
          .toISOString()
          .replace(/\.\d{3}/, "")
          .replace("T", " ")
          .replace("Z", "")
          .replaceAll(":", "-")
          .replace(" ", "_")
      );
  };

  const handleOpenSnackbarErro = () => {
    setOpenSnackbarErro(true);
  };

  const handleCloseSnackbarErro = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnackbarErro(false);
  };

  const handleDate = (data) => {
    if (!data) {
      setDataInicio(undefined);
      return;
    }
    const splitData = data.toLocaleDateString("pt-BR").split("/");
    if (!splitData[2] || !splitData[1] || splitData[2].length < 4) {
      setDataInicio(undefined);
      return;
    }
    const novaData = `${splitData[2]}-${splitData[1]}`;
    setDataInicio(novaData);
  };

  const handleGrupoECnpjChange = (ids) => {
    setGrupoECnpj(ids);
  };

  const handleDebounceChange = async (event) => {
    setLoading(true);
    if (grupoECnpj.CNPJ) {
      try {
        const response = await obterFolhasManuaisPorTexto(
          grupoECnpj.CNPJ,
          dataInicio,
          event.target.value
        );
        await handleTratarDados(grupoECnpj.CNPJ, response.folha);
      } catch (err) {
        if (err.response) {
          setMsg(err.response.data.mensagem);
          handleOpenSnackbarErro();
        } else {
          setMsg("Erro: Tente novamente!");
          handleOpenSnackbarErro();
        }
      }
    } else {
      setMsg("Necessário selecionar uma Empresa.");
      handleOpenSnackbarErro();
    }
    setLoading(false);
  };

  const handleSomatoria = async (somatoria) => {
    try {
      await ajustarSomatoriasFolhaManual(grupoECnpj.CNPJ, somatoria);
      if (!!somatoria.somatorias.somaBaseCalculo) {
        setBaseCalculo(somatoria.somatorias.somaBaseCalculo);
      }
      if (!!somatoria.somatorias.somaProvento) {
        setProvento(somatoria.somatorias.somaProvento);
      }
      if (!!somatoria.somatorias.somaDesconto) {
        setDesconto(somatoria.somatorias.somaDesconto);
      }
      return true;
    } catch (err) {
      if (err.response) {
        setMsg(err.response.data.mensagem);
        handleOpenSnackbarErro();
      } else {
        setMsg("Erro: Tente novamente!");
        handleOpenSnackbarErro();
      }
      return false;
    }
  };

  const handleSomatoriaBaseCalculo = async (value) => {
    return await handleSomatoria({
      somatorias: {
        periodoApuracao: dataInicio,
        somaBaseCalculo: value,
        somaProvento: null,
        somaDesconto: null,
      },
    });
  };

  const handleSomatoriaProvento = async (value) => {
    return await handleSomatoria({
      somatorias: {
        periodoApuracao: dataInicio,
        somaBaseCalculo: null,
        somaProvento: value,
        somaDesconto: null,
      },
    });
  };

  const handleSomatoriaDesconto = async (value) => {
    return await handleSomatoria({
      somatorias: {
        periodoApuracao: dataInicio,
        somaBaseCalculo: null,
        somaProvento: null,
        somaDesconto: value,
      },
    });
  };

  return (
    <div>
      <Main />

      <CRUDPageHeader
        headerIcon={
          <>
            <RequestPageIcon fontSize="30px" />
          </>
        }
        headerTitle="Folha Manual"
        disableModalButton
        handleDate={handleDate}
        labelDate="Competência"
        datePicker
        handleDebounceChange={handleDebounceChange}
        AutoCompleteRequired={grupoECnpj.CNPJ ? false : true}
        AutoCompleteGrupoECnpj
        handleGrupoECnpjChange={handleGrupoECnpjChange}
      />

      <div className={styles.backgroundContainer}>
        <Grid
          container
          spacing={2}
          direction={"row"}
          sx={{ justifyContent: "end" }}
        >
          <Grid
            item
            xs={1}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "end",
              justifyContent: "end",
            }}
          >
            <Grid
              item
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <div className={styles.iconeSubTitulo}>
                <AssuredWorkload />
              </div>
              <h1 className={styles.subTitulo}>eSocial</h1>
            </Grid>
          </Grid>
          <Grid item xs={2}>
            <EditableBRLCurrencyField
              label={"Base de Cálculo"}
              value={baseCalculo}
              onSubmit={handleSomatoriaBaseCalculo}
              disabled={!grupoECnpj.CNPJ || !dataInicio}
            />
          </Grid>
          <Grid item xs={2}>
            <EditableBRLCurrencyField
              label={"Provento"}
              value={provento}
              onSubmit={handleSomatoriaProvento}
              disabled={!grupoECnpj.CNPJ || !dataInicio}
            />
          </Grid>
          <Grid item xs={2}>
            <EditableBRLCurrencyField
              label={"Desconto"}
              value={desconto}
              onSubmit={handleSomatoriaDesconto}
              disabled={!grupoECnpj.CNPJ || !dataInicio}
            />
          </Grid>
          <Grid
            item
            xs={6}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "end",
              justifyContent: "end",
            }}
          >
            <Grid
              item
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <div className={styles.iconeSubTitulo}>
                <BorderColor />
              </div>
              <h1 className={styles.subTitulo}>Conferência</h1>
            </Grid>
          </Grid>
          <Grid item xs={2}>
            <GenericTextField
              label="Base de Cálculo"
              value={diferencaBaseCalculo}
              disabled
              variant="standard"
              InputProps={{
                inputComponent: BRLCurrencyFixedFormat,
              }}
              sx={{
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor:
                    !grupoECnpj.CNPJ || !dataInicio ? null : "#777777",
                },
              }}
            />
          </Grid>
          <Grid item xs={2}>
            <GenericTextField
              label="Provento"
              value={diferencaProvento}
              disabled
              variant="standard"
              InputProps={{
                inputComponent: BRLCurrencyFixedFormat,
              }}
              sx={{
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor:
                    !grupoECnpj.CNPJ || !dataInicio ? null : "#777777",
                },
              }}
            />
          </Grid>
          <Grid item xs={2}>
            <GenericTextField
              label="Desconto"
              value={diferencaDesconto}
              disabled
              variant="standard"
              InputProps={{
                inputComponent: BRLCurrencyFixedFormat,
              }}
              sx={{
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor:
                    !grupoECnpj.CNPJ || !dataInicio ? null : "#777777",
                },
              }}
            />
          </Grid>
        </Grid>
      </div>

      <TabelaDinamica
        data={data}
        columns={columns}
        loading={loading}
        exportFilename={exportFilename}
        exportData={exportData}
        disableDelete
        disableEdit
      />

      <MensagemErro
        open={openSnackbarErro}
        handleClose={handleCloseSnackbarErro}
        msg={msg}
      />
    </div>
  );
};

export default FolhasManuais;
